import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Check, ChevronDown, ChevronRight, FileText, Loader2, Search } from 'lucide-react';
import { useState } from 'react';
function parseResult(result) {
    try {
        return JSON.parse(result);
    } catch  {
        return null;
    }
}
function parseArgs(args) {
    if (!args) return null;
    try {
        return JSON.parse(args);
    } catch  {
        return null;
    }
}
function truncateFilename(filename, maxLength = 40) {
    if (filename.length <= maxLength) return filename;
    const ext = filename.split('.').pop() || '';
    const nameWithoutExt = filename.slice(0, filename.length - ext.length - 1);
    const truncatedName = `${nameWithoutExt.slice(0, maxLength - ext.length - 4)}...`;
    return `${truncatedName}.${ext}`;
}
/**
 * Beautiful card for displaying read-document-chunks results
 */ export function ReadDocumentChunksCard({ toolCall }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const isComplete = !!toolCall.result;
    const hasError = !!toolCall.error;
    const parsed = toolCall.result ? parseResult(toolCall.result) : null;
    const args = parseArgs(toolCall.arguments);
    if (hasError) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-red-500 bg-red-500/5 border border-red-500/20 rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(FileText, {
                    className: "h-4 w-4"
                }),
                /*#__PURE__*/ _jsxs("span", {
                    children: [
                        "Failed to read document: ",
                        toolCall.error
                    ]
                })
            ]
        });
    }
    if (!isComplete) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted/30 border rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(Loader2, {
                    className: "h-4 w-4 animate-spin text-muted-foreground"
                }),
                /*#__PURE__*/ _jsxs("span", {
                    children: [
                        "Reading document",
                        args?.search_query ? ` (searching: "${args.search_query}")` : '',
                        "..."
                    ]
                })
            ]
        });
    }
    if (!parsed || !parsed.chunks || !Array.isArray(parsed.chunks)) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted/30 border rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(FileText, {
                    className: "h-4 w-4"
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: "Document read complete"
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "border rounded-lg overflow-hidden bg-muted/20",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between px-3 py-2 bg-muted/30 border-b cursor-pointer hover:bg-muted/40 transition-colors",
                onClick: ()=>setIsExpanded(!isExpanded),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "p-1.5 rounded-md bg-muted flex-shrink-0",
                                children: /*#__PURE__*/ _jsx(FileText, {
                                    className: "h-4 w-4 text-foreground"
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-sm font-medium truncate block",
                                        children: truncateFilename(parsed.filename)
                                    }),
                                    args?.search_query && /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-1 text-xs text-muted-foreground",
                                        children: [
                                            /*#__PURE__*/ _jsx(Search, {
                                                className: "h-3 w-3"
                                            }),
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "truncate",
                                                children: [
                                                    '"',
                                                    args.search_query,
                                                    '"'
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 flex-shrink-0",
                        children: [
                            /*#__PURE__*/ _jsxs(Badge, {
                                variant: "secondary",
                                className: "text-xs",
                                children: [
                                    parsed.chunks_returned,
                                    " chunk",
                                    parsed.chunks_returned !== 1 ? 's' : ''
                                ]
                            }),
                            parsed.more_chunks_available && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-xs text-amber-600 border-amber-300",
                                children: "+more"
                            }),
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-4 w-4 text-green-500"
                            }),
                            isExpanded ? /*#__PURE__*/ _jsx(ChevronDown, {
                                className: "h-4 w-4 text-muted-foreground"
                            }) : /*#__PURE__*/ _jsx(ChevronRight, {
                                className: "h-4 w-4 text-muted-foreground"
                            })
                        ]
                    })
                ]
            }),
            isExpanded && /*#__PURE__*/ _jsxs("div", {
                className: "p-3 space-y-3",
                children: [
                    parsed.metadata && /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-2 text-xs text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    parsed.metadata.chunk_count,
                                    " total chunks"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: "•"
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    parsed.metadata.word_count.toLocaleString(),
                                    " words"
                                ]
                            })
                        ]
                    }),
                    parsed.hint && /*#__PURE__*/ _jsxs("div", {
                        className: "text-xs text-muted-foreground bg-muted/50 rounded p-2",
                        children: [
                            "💡 ",
                            parsed.hint
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            parsed.chunks.slice(0, 3).map((chunk, idx)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "bg-background/50 rounded p-2 border",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "flex items-center gap-2 mb-1",
                                            children: /*#__PURE__*/ _jsxs(Badge, {
                                                variant: "outline",
                                                className: "text-[10px] px-1.5 py-0",
                                                children: [
                                                    "Chunk ",
                                                    chunk.index
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "text-xs text-muted-foreground line-clamp-3",
                                            children: [
                                                chunk.content.slice(0, 300),
                                                chunk.content.length > 300 ? '...' : ''
                                            ]
                                        })
                                    ]
                                }, idx)),
                            parsed.chunks.length > 3 && /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-muted-foreground text-center py-1",
                                children: [
                                    "+",
                                    parsed.chunks.length - 3,
                                    " more chunks"
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
